import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FileDisplayComponent } from '../../../file-display/file-display.component';
import { RequiredDocsUploadComponent } from '../../required-docs-upload.component';
import { DisplayableFile } from '../../../interfaces/displayable-file.interface';
import { SpecialEnrollmentCircumstanceStateStructure } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/special-enrollment-circumstance.StateStructure.interface';
import { SPECIAL_ENROLLMENT_CIRCUMSTANCE_STATE_NAME } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer';
import { LIFE_EVENT_DOCUMENT_UPLOAD_REDUCER } from 'src/app/angular-elements/shared/components/configs/reducers/life-event.reducer';
import { LifeEventStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/life-event.state-structure.interface';

@Component({
    selector: 'app-required-docs-upload-special-enrollment',
    templateUrl: './required-docs-upload-special-enrollment.component.html',
    styleUrls: [
        './../../required-docs-upload.component.scss',
        './required-docs-upload-special-enrollment.component.scss'
    ],
    standalone: false
})
export class RequiredDocsUploadSpecialEnrollmentComponent extends RequiredDocsUploadComponent implements OnInit, OnDestroy {

  @Input() customConfigs: {
    formNameValue: string; // Passed along with payload to indicate type of document ( backend uses to organize files )
    allowedExtensions: string[];
    customDropdownSelection?: {
      attributeName: string;
      attributeHeader: string;
      values: { label: string; value: string; }[];
      requireAllDropdownsToHaveUniqueSelection?: boolean;
      disableRemoveButtonOnSuccess?: boolean;
    }; // This allows the uesr to select a formNameValue
    minDocuments?: number;
    maxDocuments?: number;
    fifthBullet?: string;
    additionalInstructions?: string;
    singleDocumentControlNumberExperience?: boolean; // drives whether we upload all documents as the specific [0]documentControlNumber 
    editorName: string; //specially for sep review experience
  };

  // Used during the SEP-REVIEW-GRID flow && the enroll/application/special-enrollment-online flow
  // Will only be defined on init in SEP-REVIEW-GRID flow; must manually populate during enroll/ flow
  @Input() singleDocumentControlNumber: string;

  /** Maintains reference to dropdown controls, gives a form to observe */
  customDropdownForm: FormArray;
  /** Assign value to disabledUploadDueToDropdownLogic when appropriate */
  customDropdownFormChanges: Subscription;
  /** Disable UPLOAD ALL and UPLOAD (file-display.component) when true */
  disabledUploadDueToDropdownLogic: boolean;
  disableUploadAllDueToDropdownLogic: boolean;

  constructor() {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();

    /** Loops through all existing dropdowns and checks their values, custom login within */
    if (this.customConfigs?.customDropdownSelection) {
      this.displayedColumns.splice(1, 0, this.customConfigs.customDropdownSelection.attributeHeader);

      this.customDropdownForm = this.fb.array([]);
      this.disableUploadAllDueToDropdownLogic = false;

      this.customDropdownFormChanges = this.customDropdownForm.valueChanges.subscribe(v => {
        
        const controlValues = [];

        /** 
         * Loop through controls
         * If valid, track the value
         * If invalid, disable the UPLOAD ALL BUTTON
         */
        for (let i = 0; i < this.customDropdownForm.length; i++) {
          
          const control = this.customDropdownForm.at(i);
          const cv = control.value;
          
          if (control.valid) {
            controlValues.push(cv);
          } else {
            this.disableUploadAllDueToDropdownLogic = true; 
          }
        }

        /**
         * If enforcing unique document types - confirm #-of-controls matches unique-control-values
         * Else, if the form is fully valid, enable the UPLOAD ALL
         */
        if (this.customConfigs?.customDropdownSelection.requireAllDropdownsToHaveUniqueSelection) {
          /** All dropdown values must be unique - possibly spin this logic into a configurable */
          this.disabledUploadDueToDropdownLogic = !this.customDropdownForm.valid || (this.customDropdownForm.controls.length !== (new Set(controlValues)).size);
        } else if (this.customDropdownForm.valid) {
          this.disableUploadAllDueToDropdownLogic = false;
        }

      });

    }

    /** Pre-populated existing documents if available */
    if (this.existingDocs) {
      
      if (this.customDropdownForm) {
        this.existingDocs.forEach(d => {
          this.customDropdownForm.push(
            this.fb.control(d.doctype, [Validators.required])
          );
          this.customDropdownForm.controls[this.customDropdownForm.controls.length - 1].disable();
        })
      }

    }

  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.customDropdownFormChanges) { this.customDropdownFormChanges.unsubscribe(); }
  }

  extraFileInputBehavior() {
    if (this.customDropdownForm) {
      this.customDropdownForm.push(
        this.fb.control(null, [Validators.required])
      );
    }
  }
  extraFileRemovalBehavior(fileComponent: FileDisplayComponent) {
    if (this.customDropdownForm) {
      this.customDropdownForm.removeAt(fileComponent.index);
    }
  }

  addPayloadAdditions(): (formData: FormData, displayableFileObj: DisplayableFile) => void {
    return (formData: FormData, displayableFileObj: DisplayableFile) => {
      
      /** 
       * If needed, merge documents via their shared 'documentControlNumber'
       */
      
      const editorName = this.customConfigs?.editorName ?? null;
      formData.append(
        'editorName', 
        editorName
      );

      // ...enroll/application/special-enrollment-online - forms-as-json - singleDocumentControlNumberExperience determines sharing
      if (this.customConfigs?.singleDocumentControlNumberExperience) {
        const lifeEventState: LifeEventStateStructure = this.reducerUtilityService.getComponentStates()[LIFE_EVENT_DOCUMENT_UPLOAD_REDUCER];
        if (lifeEventState.documents?.[0]) {
          // This value won't exist on @Input in this flow
          this.singleDocumentControlNumber = lifeEventState.documents[0].displayableFileObj.documentRecord;
        }
      }
      // sep-review && ..enroll/application/special-enrollment-online
      // DON'T MERGE THIS LOGIC INTO THE ABOVE, you will lose the SEP-REVIEW-GRID experience
      if (this.singleDocumentControlNumber) {
        formData.append(
          'documentControlNumber',
          this.singleDocumentControlNumber
        )
      }

      // formname
      if (this.customConfigs.formNameValue) {
        formData.append(
          'formname',
          this.customConfigs.formNameValue
        );
      }

      // description
      if (displayableFileObj.documentDescription) {
        formData.append(
          'description',
          displayableFileObj.documentDescription
        )
      }

      // lifeEvent
      const sepData: SpecialEnrollmentCircumstanceStateStructure
        = this.ngRedux.getState().payloadStates[SPECIAL_ENROLLMENT_CIRCUMSTANCE_STATE_NAME];
      if (sepData.lifeEvent?.lifeEventId) {
        formData.append(
          'lifeEvent',
          sepData.lifeEvent.lifeEventId
        )
      }

    }
  }

}
